import isEqual from 'react-fast-compare';

/** Применяем только локальную дельту: загрузка/пересчёт соседних строк после
 * открытия панели не должны быть затёрты её копией всей таблицы. */
export const mergeRowFormValue = (before: unknown, draft: unknown, current: unknown): unknown => {
    if (isEqual(before, draft)) return current;
    const isObject = (value: unknown): value is Record<string, unknown> =>
        !!value && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date);
    // Без уникального |NUM строки не сопоставить — тогда черновик пишется целиком.
    const isKeyedRows = (rows: unknown): rows is Record<string, unknown>[] =>
        Array.isArray(rows) &&
        rows.every((row) => isObject(row) && row['|NUM'] != null) &&
        new Set(rows.map((row) => row['|NUM'])).size === rows.length;
    if (isKeyedRows(before) && isKeyedRows(draft) && isKeyedRows(current)) {
        const oldRows = new Map(before.map((row) => [row['|NUM'], row]));
        const editedRows = new Map(draft.map((row) => [row['|NUM'], row]));
        const currentRows = new Map(current.map((row) => [row['|NUM'], row]));
        const reordered = !isEqual(
            [...oldRows.keys()].filter((key) => editedRows.has(key)),
            [...editedRows.keys()].filter((key) => oldRows.has(key)),
        );
        let keys: unknown[];
        if (reordered) {
            keys = [...editedRows.keys(), ...currentRows.keys()];
        } else {
            // Новая строка черновика встаёт за той же соседкой, что и в черновике
            // (тиражирование, перетаскивание), а не в конец таблицы.
            const insertedAfter = new Map<unknown, unknown[]>();
            let anchor: unknown = undefined;
            for (const key of editedRows.keys()) {
                if (oldRows.has(key)) {
                    if (currentRows.has(key)) anchor = key;
                } else if (insertedAfter.has(anchor)) insertedAfter.get(anchor)!.push(key);
                else insertedAfter.set(anchor, [key]);
            }
            keys = [
                ...(insertedAfter.get(undefined) ?? []),
                ...[...currentRows.keys()].flatMap((key) => [key, ...(insertedAfter.get(key) ?? [])]),
            ];
        }
        return [...new Set(keys)]
            .filter((key) => !oldRows.has(key) || (editedRows.has(key) && currentRows.has(key)))
            .map((key) =>
                editedRows.has(key)
                    ? mergeRowFormValue(oldRows.get(key), editedRows.get(key), currentRows.get(key))
                    : currentRows.get(key),
            );
    }
    if (isObject(before) && isObject(draft) && isObject(current)) {
        const result = { ...current };
        for (const key of new Set([...Object.keys(before), ...Object.keys(draft)])) {
            if (!(key in draft)) delete result[key];
            else result[key] = mergeRowFormValue(before[key], draft[key], current[key]);
        }
        return result;
    }
    return draft;
};
